/**
 * EG Scroll Reveal v2 — Apple-style scroll-linked animations
 * Scroll-driven parallax, progressive reveals, buttery easing.
 * No dependencies. GPU-accelerated. Respects prefers-reduced-motion.
 */
(function() {
  'use strict';

  // Log if reduced motion is active (don't bail — let users see animations on the site)
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  console.log('[EG-SCROLL] Reduced motion:', reducedMotion);
  // If reduced motion, use simpler fade-only animations (no transforms)
  // For now, run everything — we'll add a reduced-motion fallback later

  // ---- Utilities ----
  function lerp(a, b, t) { return a + (b - a) * t; }
  function clamp(v, min, max) { return Math.min(Math.max(v, min), max); }

  // Map scroll position to 0-1 progress through an element
  // Uses element's own position relative to viewport center
  function getProgress(el, offset) {
    var rect = el.getBoundingClientRect();
    var winH = window.innerHeight;
    var elCenter = rect.top + rect.height * 0.3; // use 30% from top of element as anchor

    // Start when element's anchor enters bottom portion of viewport
    var triggerPoint = winH * (1 - (offset || 0.2)); // trigger when 20% into viewport
    // End when anchor reaches upper quarter — longer travel = smoother feel
    var endPoint = winH * 0.15;

    var raw = (triggerPoint - elCenter) / (triggerPoint - endPoint);
    return clamp(raw, 0, 1);
  }

  // Ultra-smooth easing — slow start, gentle arrival, no snap
  function easeOut(t) { return 1 - Math.pow(1 - t, 2.5); }  // softer than cubic
  function easeOutQuart(t) { return 1 - Math.pow(1 - t, 3); }
  // Smooth both ends — no jerk at start OR end
  function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

  // ---- Animation Registry ----
  var animations = [];
  var ticking = false;

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(updateAll);
      ticking = true;
    }
  }

  function updateAll() {
    ticking = false;
    for (var i = 0; i < animations.length; i++) {
      animations[i].update();
    }
  }

  // ---- Animation Types ----

  // Fade up with parallax — text rises and fades in as you scroll
  function createFadeUp(el, opts) {
    var distance = opts.distance || 120;
    var done = false;
    el.style.opacity = '0';
    el.style.transform = 'translateY(' + distance + 'px)';
    el.style.willChange = 'opacity, transform';

    return {
      update: function() {
        if (done) return;
        var p = easeInOut(getProgress(el, opts.offset || 0.1));
        el.style.opacity = p;
        el.style.transform = 'translateY(' + lerp(distance, 0, p) + 'px)';
        if (p >= 1) { done = true; el.style.willChange = 'auto'; }
      }
    };
  }

  // Scale reveal — image grows from small + faded to full
  function createScaleReveal(el, opts) {
    var startScale = opts.startScale || 0.88;
    var done = false;
    el.style.opacity = '0';
    el.style.transform = 'scale(' + startScale + ')';
    el.style.willChange = 'opacity, transform';

    return {
      update: function() {
        if (done) return;
        var p = easeOutQuart(getProgress(el, 0.05));
        el.style.opacity = clamp(p * 1.5, 0, 1);  // fade in faster than scale
        el.style.transform = 'scale(' + lerp(startScale, 1, p) + ')';
        if (p >= 1) { done = true; el.style.willChange = 'auto'; }
      }
    };
  }

  // Parallax image — image moves slower than scroll
  function createParallax(el, opts) {
    var img = el.querySelector('img, video, .v2__fullscreen-img');
    if (!img) return null;
    var speed = opts.speed || 0.3;  // 0 = no parallax, 1 = full speed
    img.style.willChange = 'transform';

    return {
      update: function() {
        var rect = el.getBoundingClientRect();
        var winH = window.innerHeight;
        var center = (rect.top + rect.height / 2 - winH / 2) / winH;
        var offset = center * speed * 60;
        img.style.transform = 'translateY(' + offset + 'px)';
      }
    };
  }

  // Split reveal — two halves slide in from opposite sides
  function createSplitReveal(el) {
    var grid = el.querySelector('.v2__split-grid');
    if (!grid || grid.children.length < 2) return null;
    var isReverse = grid.classList.contains('v2__split-grid--reverse');
    var left = grid.children[0];
    var right = grid.children[1];
    var dist = 100;
    var done = false;

    left.style.opacity = '0';
    left.style.transform = 'translateX(' + (isReverse ? dist : -dist) + 'px)';
    left.style.willChange = 'opacity, transform';
    right.style.opacity = '0';
    right.style.transform = 'translateX(' + (isReverse ? -dist : dist) + 'px)';
    right.style.willChange = 'opacity, transform';

    return {
      update: function() {
        if (done) return;
        var p = easeInOut(getProgress(el, 0.08));
        // Left side leads slightly
        var pLeft = easeOut(clamp(p * 1.15, 0, 1));
        var pRight = easeOut(clamp((p - 0.08) * 1.15, 0, 1));

        left.style.opacity = pLeft;
        left.style.transform = 'translateX(' + lerp(isReverse ? dist : -dist, 0, pLeft) + 'px)';
        right.style.opacity = pRight;
        right.style.transform = 'translateX(' + lerp(isReverse ? -dist : dist, 0, pRight) + 'px)';

        if (p >= 1) {
          done = true;
          left.style.willChange = 'auto';
          right.style.willChange = 'auto';
        }
      }
    };
  }

  // Stagger children — cards materialize one by one
  function createStagger(el, opts) {
    var selector = opts.childSelector;
    var children = selector ? el.querySelectorAll(selector) : [];
    if (!children.length) children = el.querySelectorAll('[class*="grid"] > *, [class*="carousel"] > *');
    if (!children.length) return null;

    var dir = opts.direction || 'up';  // 'up', 'left', 'sides'
    var distance = opts.distance || 80;
    var staggerAmt = opts.stagger || 0.06;  // as fraction of progress
    var done = false;

    children.forEach(function(child, i) {
      child.style.opacity = '0';
      child.style.willChange = 'opacity, transform, filter';
      if (dir === 'left') {
        child.style.transform = 'translateX(-' + distance + 'px) scale(0.92)';
        child.style.filter = 'blur(4px)';
      } else if (dir === 'sides') {
        child.style.transform = 'translateX(' + (i % 2 === 0 ? -distance : distance) + 'px)';
      } else {
        child.style.transform = 'translateY(' + distance + 'px)';
      }
    });

    return {
      update: function() {
        if (done) return;
        var baseP = getProgress(el, 0.08);
        var allDone = true;

        children.forEach(function(child, i) {
          var childP = easeOut(clamp((baseP - i * staggerAmt) / (1 - children.length * staggerAmt * 0.5), 0, 1));
          child.style.opacity = childP;

          if (dir === 'left') {
            child.style.transform = 'translateX(' + lerp(-distance, 0, childP) + 'px) scale(' + lerp(0.92, 1, childP) + ')';
            child.style.filter = 'blur(' + lerp(4, 0, childP) + 'px)';
          } else if (dir === 'sides') {
            var d = i % 2 === 0 ? -distance : distance;
            child.style.transform = 'translateX(' + lerp(d, 0, childP) + 'px)';
          } else {
            child.style.transform = 'translateY(' + lerp(distance, 0, childP) + 'px)';
          }

          if (childP < 1) allDone = false;
        });

        if (allDone) {
          done = true;
          children.forEach(function(child) { child.style.willChange = 'auto'; child.style.filter = ''; });
        }
      }
    };
  }

  // Statement text — lines reveal with stagger + slight scale
  function createTextReveal(el) {
    // Find the main text content
    var textEl = el.querySelector('.v2__statement-text');
    var monoEl = el.querySelector('.v2__mono');
    var ctaEl = el.querySelector('.v2__underline-cta, a[class*="cta"]');
    var parts = [];

    if (monoEl) parts.push(monoEl);
    if (textEl) {
      // Split text into lines by <br> tags
      var lines = textEl.innerHTML.split(/<br\s*\/?>/gi);
      if (lines.length > 1) {
        textEl.innerHTML = lines.map(function(line) {
          return '<span class="eg-line-reveal" style="display:block">' + line.trim() + '</span>';
        }).join('');
        var spans = textEl.querySelectorAll('.eg-line-reveal');
        spans.forEach(function(s) { parts.push(s); });
      } else {
        parts.push(textEl);
      }
    }
    if (ctaEl) parts.push(ctaEl);

    if (!parts.length) return createFadeUp(el, { distance: 100 });

    var done = false;
    parts.forEach(function(p) {
      p.style.opacity = '0';
      p.style.transform = 'translateY(50px)';
      p.style.willChange = 'opacity, transform';
    });

    return {
      update: function() {
        if (done) return;
        var baseP = getProgress(el, 0.1);
        var allDone = true;

        parts.forEach(function(part, i) {
          var p = easeInOut(clamp((baseP - i * 0.08) / 0.7, 0, 1));
          part.style.opacity = p;
          part.style.transform = 'translateY(' + lerp(50, 0, p) + 'px)';
          if (p < 1) allDone = false;
        });

        if (allDone) {
          done = true;
          parts.forEach(function(p) { p.style.willChange = 'auto'; });
        }
      }
    };
  }

  // Section with header — Apple-style: heading reveals first, then cards follow
  function createSectionWithHeader(el, opts) {
    var header = el.querySelector(opts.headerSelector);
    var selector = opts.childSelector;
    var children = selector ? el.querySelectorAll(selector) : [];
    if (!children.length) children = el.querySelectorAll('[class*="grid"] > *');

    var distance = opts.distance || 80;
    var done = false;

    // Header parts — break into label, heading, subtext
    var headerParts = [];
    if (header) {
      var mono = header.querySelector('.v2__mono, [class*="mono"]');
      var h2 = header.querySelector('h2, h3');
      var sub = header.querySelector('p');
      if (mono) headerParts.push(mono);
      if (h2) headerParts.push(h2);
      if (sub) headerParts.push(sub);
    }

    // Set initial states
    headerParts.forEach(function(part) {
      part.style.opacity = '0';
      part.style.transform = 'translateY(40px)';
      part.style.willChange = 'opacity, transform';
    });

    children.forEach(function(child) {
      child.style.opacity = '0';
      child.style.transform = 'translateY(' + distance + 'px)';
      child.style.willChange = 'opacity, transform';
    });

    // Total sequence: header parts first, then cards
    var headerCount = headerParts.length;
    var headerStagger = 0.06;
    var cardStartDelay = headerCount * headerStagger + 0.08; // cards start after header finishes
    var cardStagger = opts.stagger || 0.08;

    return {
      update: function() {
        if (done) return;
        var baseP = getProgress(el, 0.25);
        var allDone = true;

        // Animate header parts first
        headerParts.forEach(function(part, i) {
          var p = easeInOut(clamp((baseP - i * headerStagger) / 0.5, 0, 1));
          part.style.opacity = p;
          part.style.transform = 'translateY(' + lerp(40, 0, p) + 'px)';
          if (p < 1) allDone = false;
        });

        // Then animate cards
        children.forEach(function(child, i) {
          var p = easeInOut(clamp((baseP - cardStartDelay - i * cardStagger) / 0.6, 0, 1));
          child.style.opacity = p;
          child.style.transform = 'translateY(' + lerp(distance, 0, p) + 'px)';
          if (p < 1) allDone = false;
        });

        if (allDone) {
          done = true;
          headerParts.forEach(function(p) { p.style.willChange = 'auto'; });
          children.forEach(function(c) { c.style.willChange = 'auto'; });
        }
      }
    };
  }

  // ---- Section Configuration ----
  function setupSection(el, className) {
    var anim;

    switch (className) {
      case 'v2__logo-bar':
        anim = createFadeUp(el, { distance: 60, offset: 0.25 });
        break;

      case 'v2__statement':
        anim = createTextReveal(el);
        break;

      case 'v2__collections':
        anim = createStagger(el, {
          childSelector: '.v2__card',
          direction: 'left',
          distance: 100,
          stagger: 0.07
        });
        break;

      case 'v2__fullscreen':
        // Fade in only — no scale to preserve layout
        anim = createFadeUp(el, { distance: 60, offset: 0.2 });
        break;

      case 'v2__products':
        anim = createStagger(el, {
          childSelector: '.v2__product-card',
          direction: 'up',
          distance: 80,
          stagger: 0.05
        });
        break;

      case 'v2__image-pair':
        anim = createStagger(el, {
          childSelector: '.v2__pair-item, .v2__pair-grid > *',
          direction: 'sides',
          distance: 100,
          stagger: 0.1
        });
        break;

      case 'v2__split':
        anim = createSplitReveal(el);
        break;

      case 'v2__spotlight':
        anim = createFadeUp(el, { distance: 80, offset: 0.2 });
        break;

      case 'v2__press':
        anim = createSectionWithHeader(el, {
          headerSelector: '.v2__press-header',
          childSelector: '.v2__press-item, .v2__press-grid > *',
          direction: 'up',
          distance: 70,
          stagger: 0.08
        });
        break;

      case 'v2__reviews':
        anim = createSectionWithHeader(el, {
          headerSelector: '.v2__reviews-header',
          childSelector: '.v2__review-card',
          direction: 'up',
          distance: 90,
          stagger: 0.09
        });
        break;

      case 'v2__newsletter':
        anim = createFadeUp(el, { distance: 80 });
        break;

      case 'v2__pullquote':
        anim = createTextReveal(el);
        break;

      default:
        anim = createFadeUp(el, { distance: 100 });
    }

    if (anim) animations.push(anim);
  }

  // ---- Init ----
  function init() {
    console.log('[EG-SCROLL] init running, document.readyState:', document.readyState);
    var sectionTypes = [
      'v2__logo-bar', 'v2__statement', 'v2__collections', 'v2__fullscreen',
      'v2__products', 'v2__image-pair', 'v2__split', 'v2__spotlight',
      'v2__press', 'v2__reviews', 'v2__newsletter', 'v2__pullquote'
    ];

    var totalFound = 0;
    sectionTypes.forEach(function(className) {
      var elements = document.querySelectorAll('.' + className);
      if (elements.length) console.log('[EG-SCROLL] Found', elements.length, 'elements for', className);
      totalFound += elements.length;
      elements.forEach(function(el) {
        if (el.dataset.egReveal) return;
        el.dataset.egReveal = className;
        setupSection(el, className);
      });
    });
    console.log('[EG-SCROLL] Total sections found:', totalFound, '| Animations registered:', animations.length);

    // Run once immediately to set initial states + animate anything already visible
    updateAll();

    // Bind scroll — passive for performance
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  function safeInit() {
    if (window.__egScrollInit) return;
    window.__egScrollInit = true;
    setTimeout(init, 150);
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    safeInit();
  }
  document.addEventListener('DOMContentLoaded', safeInit);
  window.addEventListener('load', safeInit);
})();
